import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { ExternalLink, X, Search, Loader2, Trash2 } from "lucide-react";
import * as api from "../../lib/api";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "../ui/sheet";
import {
  PRIMARY, STATUS_META, PRIORITY_META, ACTION_META, MemberAvatar, relativeTime,
} from "./shared";

const fmt = (n, d = 0) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
const prop = "text-[10px] tracking-[0.1em] uppercase font-bold text-zinc-400";
const input = "w-full px-2 py-1 text-xs rounded-md border border-zinc-200 bg-white disabled:bg-zinc-50 disabled:text-zinc-500 focus:outline-none focus:ring-2";

function Delta({ before, after, invert = false, digits = 0 }) {
  if (before == null || after == null) return null;
  const d = after - before;
  if (Math.abs(d) < (digits ? 0.05 : 1)) return null;
  const good = invert ? d < 0 : d > 0;
  return (
    <span className={`ml-1 text-[10px] font-bold ${good ? "text-green-600" : "text-red-500"}`}>
      {d > 0 ? "+" : ""}{fmt(d, digits)}
    </span>
  );
}

export default function TaskDrawer({ taskKey, onClose, members, byId, canEdit, onChanged }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [editing, setEditing] = useState(null); // {id, body}
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    if (!taskKey) return;
    try {
      setData(await api.getTask(taskKey));
      setError("");
    } catch (e) {
      setError(e?.response?.status === 404 ? "Task not found." : "Failed to load task.");
    }
  }, [taskKey]);

  useEffect(() => { setData(null); setConfirmDelete(false); setEditing(null); load(); }, [load]);

  const task = data?.task;
  const editable = canEdit && data?.can_edit !== false;

  const save = async (patch) => {
    try {
      await api.updateTask(task.key, patch);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save.");
    }
  };
  // Text fields save on blur, only when changed.
  const saveIfChanged = (field, value) => {
    const v = typeof value === "string" ? value.trim() : value;
    if ((task[field] ?? "") !== (v ?? "")) save({ [field]: v === "" && field !== "description" ? null : v });
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.addTaskComment(task.key, comment.trim());
      setComment("");
      await load();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to post comment.");
    } finally {
      setPosting(false);
    }
  };

  const saveEdit = async () => {
    if (!editing?.body.trim()) return;
    await api.updateTaskComment(task.key, editing.id, editing.body.trim()).catch(() => {});
    setEditing(null);
    load();
  };

  const removeComment = async (id) => {
    await api.deleteTaskComment(task.key, id).catch(() => {});
    load();
    onChanged?.();
  };

  const removeQuery = async (q) => {
    await api.removeTaskQueries(task.key, [q]).catch(() => {});
    load();
    onChanged?.();
  };

  const handleDelete = async () => {
    await api.deleteTask(task.key);
    onChanged?.();
    onClose();
  };

  // Same hand-off as Topic Clusters → Queries: preset the Queries tab's persisted filter, then go.
  const viewInSearchPerformance = () => {
    try {
      localStorage.setItem("sp:queries.taskFilter", JSON.stringify(task.key));
      localStorage.setItem("sp:tab", JSON.stringify("queries"));
    } catch {}
    navigate("/dashboard/search-performance");
  };

  return (
    <Sheet open={!!taskKey} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0">
        {!task ? (
          <div className="p-6 text-sm text-zinc-400">
            <SheetTitle className="sr-only">Task</SheetTitle>
            {error || "Loading…"}
          </div>
        ) : (
          <div className="flex flex-col min-h-full">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-zinc-100">
              <SheetDescription className="text-[11px] font-bold text-zinc-400">{task.key}</SheetDescription>
              <SheetTitle className="sr-only">{task.title}</SheetTitle>
              <input
                key={`title-${task.updated_at}`}
                defaultValue={task.title}
                disabled={!editable}
                aria-label="Task title"
                onBlur={(e) => e.target.value.trim() && saveIfChanged("title", e.target.value)}
                className="w-full mt-1 text-lg font-bold text-zinc-900 bg-transparent rounded focus:outline-none focus:bg-zinc-50 disabled:text-zinc-900"
              />
              {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            </div>

            {/* Properties */}
            <div className="px-6 py-4 grid grid-cols-[110px_1fr] gap-x-4 gap-y-2.5 items-center border-b border-zinc-100">
              <span className={prop}>Status</span>
              <select value={task.status} disabled={!editable} onChange={(e) => save({ status: e.target.value })} className={input} style={{ "--tw-ring-color": PRIMARY }}>
                {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
              </select>

              <span className={prop}>Priority</span>
              <select value={task.priority} disabled={!editable} onChange={(e) => save({ priority: e.target.value })} className={input}>
                {Object.entries(PRIORITY_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
              </select>

              <span className={prop}>Assignee</span>
              <div className="flex items-center gap-2">
                <MemberAvatar member={byId[task.assignee_user_id]} size={22} />
                <select
                  value={task.assignee_user_id || ""}
                  disabled={!editable}
                  onChange={(e) => save({ assignee_user_id: e.target.value || null })}
                  className={input}
                >
                  <option value="">Unassigned</option>
                  {members.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                </select>
              </div>

              <span className={prop}>Action</span>
              <select value={task.action_type} disabled={!editable} onChange={(e) => save({ action_type: e.target.value })} className={input}>
                {Object.entries(ACTION_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
              </select>

              <span className={prop}>Due</span>
              <input
                type="date"
                value={task.due_date || ""}
                disabled={!editable}
                onChange={(e) => save({ due_date: e.target.value || null })}
                className={input}
              />

              <span className={prop}>Target page</span>
              <div className="flex items-center gap-1.5">
                <input
                  key={`url-${task.updated_at}`}
                  defaultValue={task.target_url || ""}
                  disabled={!editable}
                  placeholder="https://…"
                  onBlur={(e) => saveIfChanged("target_url", e.target.value)}
                  className={input}
                />
                {task.target_url && (
                  <a href={task.target_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-zinc-700">
                    <ExternalLink size={13} />
                  </a>
                )}
              </div>

              <span className={prop}>Labels</span>
              <input
                key={`labels-${task.updated_at}`}
                defaultValue={(task.labels || []).join(", ")}
                disabled={!editable}
                placeholder="comma separated"
                onBlur={(e) => {
                  const labels = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                  if (labels.join(",") !== (task.labels || []).join(",")) save({ labels });
                }}
                className={input}
              />
            </div>

            {/* Description */}
            <div className="px-6 py-4 border-b border-zinc-100">
              <p className={`${prop} mb-1.5`}>Description</p>
              <textarea
                key={`desc-${task.updated_at}`}
                defaultValue={task.description}
                disabled={!editable}
                rows={3}
                placeholder={editable ? "Add context, a brief, or acceptance criteria…" : ""}
                onBlur={(e) => saveIfChanged("description", e.target.value)}
                className={`${input} text-sm resize-y`}
              />
            </div>

            {/* Linked queries */}
            <div className="px-6 py-4 border-b border-zinc-100">
              <div className="flex items-center justify-between mb-2">
                <p className={prop}>Queries ({data.queries.length})</p>
                {data.queries.length > 0 && (
                  <button onClick={viewInSearchPerformance} className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-900">
                    <Search size={11} /> View in Search Performance
                  </button>
                )}
              </div>
              {data.queries.length === 0 ? (
                <p className="text-xs text-zinc-400">No queries linked. Select queries in Search Performance → Add to task.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[9px] tracking-[0.1em] uppercase text-zinc-400">
                      <th className="text-left font-bold py-1">Query</th>
                      <th className="text-right font-bold py-1">Impr. then → now</th>
                      <th className="text-right font-bold py-1">Pos. then → now</th>
                      <th className="w-6" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-50">
                    {data.queries.map(q => (
                      <tr key={q.query}>
                        <td className="py-1.5 pr-2 text-zinc-800">{q.query}</td>
                        <td className="py-1.5 text-right tabular-nums text-zinc-600 whitespace-nowrap">
                          {fmt(q.baseline?.impressions)} → {q.current ? fmt(q.current.impressions) : <span className="text-zinc-300" title="No data in the current period">—</span>}
                          <Delta before={q.baseline?.impressions} after={q.current?.impressions} />
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-zinc-600 whitespace-nowrap">
                          {fmt(q.baseline?.position, 1)} → {q.current ? fmt(q.current.position, 1) : <span className="text-zinc-300" title="No data in the current period">—</span>}
                          <Delta before={q.baseline?.position} after={q.current?.position} invert digits={1} />
                        </td>
                        <td className="py-1.5 text-right">
                          {editable && (
                            <button onClick={() => removeQuery(q.query)} title="Remove from task" className="text-zinc-300 hover:text-zinc-600">
                              <X size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Timeline */}
            <div className="px-6 py-4 flex-1">
              <p className={`${prop} mb-3`}>Activity</p>
              <ol className="space-y-3">
                {data.timeline.map(c => {
                  const author = byId[c.author_user_id];
                  const name = author?.name || (c.author_user_id === "integration" ? "AI agent" : "Someone");
                  if (c.kind === "event") {
                    return (
                      <li key={c.id} className="flex items-center gap-2 text-[11px] text-zinc-400">
                        <MemberAvatar member={author} size={16} />
                        <span><span className="font-bold text-zinc-500">{name}</span> {c.body}</span>
                        <span className="ml-auto whitespace-nowrap">{relativeTime(c.created_at)}</span>
                      </li>
                    );
                  }
                  const own = user?.id === c.author_user_id;
                  return (
                    <li key={c.id} className="flex gap-2">
                      <MemberAvatar member={author} size={22} />
                      <div className="flex-1 min-w-0 rounded-lg border border-zinc-100 bg-white px-3 py-2">
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className="font-bold text-zinc-700">{name}</span>
                          <span className="text-zinc-400">{relativeTime(c.created_at)}{c.edited_at ? " · edited" : ""}</span>
                          {own && editing?.id !== c.id && (
                            <span className="ml-auto flex gap-2 text-zinc-400">
                              <button onClick={() => setEditing({ id: c.id, body: c.body })} className="hover:text-zinc-700">Edit</button>
                              <button onClick={() => removeComment(c.id)} className="hover:text-red-600">Delete</button>
                            </span>
                          )}
                        </div>
                        {editing?.id === c.id ? (
                          <div className="mt-1.5">
                            <textarea
                              value={editing.body}
                              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                              rows={2}
                              className={`${input} text-sm`}
                              autoFocus
                            />
                            <div className="flex justify-end gap-2 mt-1">
                              <button onClick={() => setEditing(null)} className="text-[11px] text-zinc-400 hover:text-zinc-700">Cancel</button>
                              <button onClick={saveEdit} className="text-[11px] font-bold" style={{ color: PRIMARY }}>Save</button>
                            </div>
                          </div>
                        ) : (
                          <p className="mt-0.5 text-sm text-zinc-800 whitespace-pre-wrap break-words">{c.body}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>

              <form onSubmit={postComment} className="mt-4">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postComment(e); }}
                  rows={2}
                  placeholder="Leave a comment… (Ctrl+Enter to send)"
                  className={`${input} text-sm`}
                  style={{ "--tw-ring-color": PRIMARY }}
                />
                <div className="flex justify-end mt-1.5">
                  <button
                    type="submit"
                    disabled={posting || !comment.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg text-white disabled:opacity-40"
                    style={{ backgroundColor: PRIMARY }}
                  >
                    {posting && <Loader2 size={12} className="animate-spin" />}
                    Comment
                  </button>
                </div>
              </form>
            </div>

            {editable && (
              <div className="px-6 py-3 border-t border-zinc-100 flex items-center justify-between text-[11px] text-zinc-400">
                <span>Created {relativeTime(task.created_at)} by {byId[task.created_by]?.name || (task.created_by === "integration" ? "AI agent" : "someone")}</span>
                {confirmDelete ? (
                  <span className="flex items-center gap-2">
                    Delete {task.key}?
                    <button onClick={handleDelete} className="font-bold text-red-600">Delete</button>
                    <button onClick={() => setConfirmDelete(false)} className="font-bold text-zinc-500">Cancel</button>
                  </span>
                ) : (
                  <button onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-1 hover:text-red-600">
                    <Trash2 size={11} /> Delete task
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
